import { collection, getDocs } from 'firebase/firestore';
import { normalizeDrillTemplate } from './drillTemplates';
import { canAccessContentItem } from './platformAccess';
import { getOrganizationMemberships, resolvePlatformRole } from './organizationContext';

function toGenericVisibility(value = '') {
  if (value === 'org' || value === 'school' || value === 'organization') {
    return 'organization';
  }

  return value || 'private';
}

function inferOwnerType(template = {}) {
  if (template.ownerType) return template.ownerType;
  if (template.visibility === 'platform' || template.accessVisibility === 'platform') {
    return 'platform';
  }
  if (template.orgId || template.organizationId || toGenericVisibility(template.visibility) === 'organization') {
    return 'organization';
  }
  return 'instructor';
}

export function normalizeDrillTemplateAccess(template = {}, currentUserOrgId = '') {
  const normalized = normalizeDrillTemplate(template, currentUserOrgId);
  const accessVisibility = toGenericVisibility(normalized.visibility);
  const ownerType = inferOwnerType({
    ...template,
    ...normalized,
    accessVisibility,
  });
  const ownerId =
    template.ownerId ||
    (ownerType === 'organization'
      ? normalized.orgId
      : ownerType === 'platform'
        ? 'platform'
        : normalized.createdBy);

  return {
    ...normalized,
    ownerType,
    ownerId: ownerId || '',
    accessVisibility,
    accessTier: template.accessTier || normalized.accessTier || 'free',
    contentPackId: template.contentPackId || normalized.contentPackId || '',
    assignedUserIds: Array.isArray(template.assignedUserIds)
      ? template.assignedUserIds.filter(Boolean)
      : Array.isArray(normalized.assignedUserIds)
        ? normalized.assignedUserIds
        : [],
    organizationId: normalized.orgId || '',
  };
}

export function buildDrillTemplateAccessContext({
  currentUser = null,
  userData = {},
  organizationMemberships = null,
  entitlements = [],
} = {}) {
  const memberships = Array.isArray(organizationMemberships)
    ? organizationMemberships
    : getOrganizationMemberships(userData);

  return {
    currentUserId: currentUser?.uid || '',
    currentOrganizationIds: memberships.map((membership) => membership.organizationId).filter(Boolean),
    entitlements,
    platformRole: resolvePlatformRole(userData),
  };
}

export function getAccessibleDrillTemplates(
  templates = [],
  accessContext = {},
  currentUserOrgId = ''
) {
  const seen = new Set();

  return (templates || [])
    .map((template) => normalizeDrillTemplateAccess(template, currentUserOrgId))
    .filter((template) =>
      canAccessContentItem(
        {
          ...template,
          visibility: template.accessVisibility,
        },
        accessContext
      )
    )
    .filter((template) => {
      if (!template.id || seen.has(template.id)) {
        return false;
      }
      seen.add(template.id);
      return true;
    })
    .sort((a, b) => (a.title || '').localeCompare(b.title || ''));
}

export async function loadAccessibleDrillTemplates(
  db,
  { currentUser = null, userData = {}, organizationMemberships = null, entitlements = [] } = {}
) {
  const snapshot = await getDocs(collection(db, 'drillTemplates'));
  const rawTemplates = (snapshot?.docs || []).map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));
  const memberships = Array.isArray(organizationMemberships)
    ? organizationMemberships
    : getOrganizationMemberships(userData);
  const accessContext = buildDrillTemplateAccessContext({
    currentUser,
    userData,
    organizationMemberships: memberships,
    entitlements,
  });

  return getAccessibleDrillTemplates(
    rawTemplates,
    accessContext,
    memberships[0]?.organizationId || ''
  );
}
